import React, { useState } from 'react';
import { Plus, Edit, Trash2, Search, TrendingUp, CheckCircle, X, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { IncomeType, CreateIncomeTypeData } from '../../lib/sdk/index';
import { getErrorMessage } from '../../lib/utils/errorMessage';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import {
  useAllIncomeTypes,
  useCreateIncomeType,
  useUpdateIncomeType,
  useDeleteIncomeType,
} from '../../hooks/useQueries';

const IncomeTypes: React.FC = () => {
  const { data: incomeTypesData = [], isLoading: loading, refetch: refetchIncomeTypes } = useAllIncomeTypes();
  const incomeTypes = Array.isArray(incomeTypesData) ? incomeTypesData : [];

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingType, setEditingType] = useState<IncomeType | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingType, setDeletingType] = useState<IncomeType | null>(null);

  const [formData, setFormData] = useState<CreateIncomeTypeData>({ name: '' });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const createMutation = useCreateIncomeType({
    onSuccess: () => {
      toast.success('Income type created successfully!');
      setShowAddModal(false);
      setFormData({ name: '' });
      setFormErrors({});
      refetchIncomeTypes();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to create income type'));
    },
  });

  const updateMutation = useUpdateIncomeType({
    onSuccess: () => {
      toast.success('Income type updated successfully!');
      setShowEditModal(false);
      setEditingType(null);
      setFormData({ name: '' });
      setFormErrors({});
      refetchIncomeTypes();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update income type'));
    },
  });

  const deleteMutation = useDeleteIncomeType({
    onSuccess: () => {
      toast.success('Income type deleted successfully!');
      setShowDeleteConfirm(false);
      setDeletingType(null);
      refetchIncomeTypes();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to delete income type'));
      setShowDeleteConfirm(false);
      setDeletingType(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { [key: string]: string } = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    if (showEditModal && editingType) {
      updateMutation.mutate({ id: editingType._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (type: IncomeType) => {
    setEditingType(type);
    setFormData({ name: type.name });
    setShowEditModal(true);
  };

  const handleDelete = (type: IncomeType) => {
    setDeletingType(type);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!deletingType) return;
    deleteMutation.mutate(deletingType._id);
  };

  const handleToggleStatus = async (type: IncomeType) => {
    try {
      const { IncomeTypesAPI } = await import('../../lib/sdk/index');
      await IncomeTypesAPI.toggleIncomeTypeStatus(type._id);
      toast.success(`Income type ${type.isActive ? 'deactivated' : 'activated'} successfully`);
      refetchIncomeTypes();
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to update income type status'));
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingType(null);
    setFormData({ name: '' });
    setFormErrors({});
  };

  const filteredTypes = incomeTypes.filter((type) => {
    const matchesSearch = type.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && type.isActive) ||
      (statusFilter === 'inactive' && !type.isActive);
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Income Types</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage income category types for departments</p>
        </div>
        <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
          Add Income Type
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{incomeTypes.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {incomeTypes.filter((t) => t.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <X className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inactive Types</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {incomeTypes.filter((t) => !t.isActive).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search income types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="form-select"
            >
              <option value="all">All Types</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            {(statusFilter !== 'all' || searchTerm) && (
              <Button variant="secondary" size="sm" onClick={() => { setStatusFilter('all'); setSearchTerm(''); }}>
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {filteredTypes.length === 0 ? (
          <div className="text-center py-12">
            <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {incomeTypes.length === 0 ? 'No income types found' : 'No types match your filters'}
            </h3>
          </div>
        ) : (
          <TableRoot>
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Status</TH>
                  <TH>Created At</TH>
                  <TH>Updated At</TH>
                  <TH>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {filteredTypes.map((type) => (
                  <TR key={type._id} className={!type.isActive ? 'bg-gray-50 dark:bg-gray-800/50' : ''}>
                    <TD className="font-medium">{type.name}</TD>
                    <TD>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          type.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}
                      >
                        {type.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </TD>
                    <TD className="text-sm text-gray-500">{formatDate(type.createdAt)}</TD>
                    <TD className="text-sm text-gray-500">{formatDate(type.updatedAt)}</TD>
                    <TD>
                      <div className="flex space-x-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(type)} leftIcon={<Edit className="h-4 w-4" />}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(type)}
                          leftIcon={type.isActive ? <X className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          className={type.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
                        >
                          {type.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(type)}
                          leftIcon={<Trash2 className="h-4 w-4" />}
                          className="text-red-600 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {showAddModal ? 'Add Income Type' : 'Edit Income Type'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter income type name"
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && <p className="text-sm text-red-600 mt-1">{formErrors.name}</p>}
              </div>
              <div className="flex space-x-3 pt-4">
                <Button type="button" variant="secondary" onClick={closeModal} className="flex-1">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="flex-1"
                >
                  {createMutation.isLoading || updateMutation.isLoading
                    ? 'Saving...'
                    : showAddModal
                    ? 'Add Type'
                    : 'Update Type'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && deletingType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Income Type</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete "{deletingType.name}"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => { setShowDeleteConfirm(false); setDeletingType(null); }} className="flex-1">
                Cancel
              </Button>
              <Button variant="error" onClick={confirmDelete} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeTypes;
